/**
 * Hook useKanbanConversationCrmRealtime - Realtime para dados CRM no Kanban de Conversas
 * 
 * Escuta mudanças nas tabelas `contracts` e `crm_deals` via Supabase Realtime
 * e invalida o cache `kanban-conversation-crm-data` para manter os cards atualizados.
 * 
 * Cenários cobertos:
 * - Contrato assinado externamente (webhook → UPDATE em contracts)
 * - Novo deal criado (INSERT em crm_deals)
 * - Deal movido de fase ou alterado de status (UPDATE em crm_deals)
 * 
 * Versão: 1.0.0 - 2026-03-07
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger condicional
const logger = createLogger('KanbanConvCrmRealtime');

// Tempo de debounce em milissegundos para evitar múltiplas invalidações em sequência
const DEBOUNCE_MS = 2000;

/**
 * Hook que subscreve Realtime nas tabelas contracts e crm_deals
 * e invalida o cache CRM do Kanban de Conversas com debounce.
 * 
 * @param tenantId - ID do tenant atual para filtrar eventos Realtime
 * @param enabled - Se deve criar a subscrição (combina crmEnabled + showCrmData)
 */
export function useKanbanConversationCrmRealtime(
  tenantId: string | undefined | null,
  enabled: boolean = true
) {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Não cria canal se desabilitado ou sem tenant
    if (!enabled || !tenantId) return;

    logger.debug('Configurando Realtime CRM para Kanban de Conversas');

    /**
     * Função com debounce para invalidar cache CRM
     * Evita flood quando múltiplos eventos chegam em sequência
     * (ex: contrato muda processing_signature → sent → signed)
     */
    const invalidateWithDebounce = (source: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        logger.debug(`Invalidando cache CRM (fonte: ${source})`);
        queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    };

    // Canal único para ambas as tabelas
    const channel = supabase
      .channel(`kanban-conv-crm-rt-${tenantId}`)
      // Escuta UPDATE em contracts (status alterado por webhook ou ação manual)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contracts',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: Contrato atualizado', {
            id: (payload.new as any)?.id,
            status: (payload.new as any)?.status,
          });
          invalidateWithDebounce('contract_update');
        }
      )
      // Escuta INSERT e UPDATE em crm_deals (novo deal ou deal alterado)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'crm_deals',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: Novo deal criado', {
            id: (payload.new as any)?.id,
          });
          invalidateWithDebounce('deal_insert');
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_deals',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: Deal atualizado', {
            id: (payload.new as any)?.id,
            status: (payload.new as any)?.status,
          });
          invalidateWithDebounce('deal_update');
        }
      )
      .subscribe((status) => {
        logger.debug(`Realtime CRM Kanban Conversas: ${status}`);
      });

    // Cleanup: remove canal e limpa debounce ao desmontar ou mudar dependências
    return () => {
      logger.debug('Removendo Realtime CRM do Kanban de Conversas');
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [tenantId, enabled, queryClient]);
}
